import { isApiError } from '../../api/client';
import type { StepTarget, UploadIn } from '../../api/partner';
import type { Step, StepIn, StepKey, UploadTicket } from '../../api/types';
import { isNetworkError } from '../../lib/errors';
import type { Prepared } from '../../lib/image';
import { markBusy, pendingStore, unmarkBusy, usePending, type PendingStore } from './pending';

/**
 * Step-with-photos flow (one press of «Забрав тіло» / «Перетнув кордон» / «Доставив»):
 *
 *   idle ──submit──▶ (no photos & not confirmed) confirm_no_photo ──submit(confirm)──▶ …
 *        ──submit──▶ uploading: for every photo  upload-url → PUT → complete   (each file on its own, retried alone)
 *                    ├─ some file failed ──▶ upload_failed ──retry──▶ uploading (only the failed files)
 *                    └─ all done ──▶ posting: POST …/steps {step_key, note, document_ids, at, lat?, lng?}
 *                                    ├─ ok ──▶ done
 *                                    ├─ no network ──▶ queued (kept in localStorage, sent later by flushPending)
 *                                    └─ other error ──▶ failed ──retry──▶ posting
 *
 * The pending step (with the ids of the photos already uploaded) is written to localStorage before the first
 * request, so closing the app or losing the network never loses a step that the driver has pressed.
 */

export type Phase = 'idle' | 'confirm_no_photo' | 'uploading' | 'upload_failed' | 'posting' | 'queued' | 'failed' | 'done';
export type FileStatus = 'waiting' | 'preparing' | 'uploading' | 'done' | 'failed';

export interface FileItem {
  key: string;
  name: string;
  status: FileStatus;
  /** 0..1 */
  progress: number;
  documentId?: string;
  error?: unknown;
}

export interface FlowState {
  phase: Phase;
  files: FileItem[];
  error: unknown;
  step: Step | null;
}

export interface StepDeps {
  uploadUrl: (t: StepTarget, body: UploadIn) => Promise<UploadTicket>;
  put: (url: string, blob: Blob, mime: string, onProgress?: (f: number) => void) => Promise<void>;
  complete: (documentId: string) => Promise<unknown>;
  addStep: (t: StepTarget, body: StepIn) => Promise<Step>;
  prepare: (file: File) => Promise<Prepared>;
  store?: PendingStore;
  now?: () => string;
  newId?: () => string;
}

export interface SubmitOptions {
  note?: string;
  confirmNoPhoto?: boolean;
  coords?: { lat: number; lng: number } | null;
}

let keySeq = 0;
const defaultId = () =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : `p-${Date.now()}-${++keySeq}`;

export class StepFlow {
  private state: FlowState = { phase: 'idle', files: [], error: null, step: null };
  private listeners = new Set<(s: FlowState) => void>();
  private blobs = new Map<string, File>();
  private pendingId: string | null = null;
  private body: StepIn | null = null;
  private readonly store: PendingStore;

  constructor(
    private readonly deps: StepDeps,
    readonly target: StepTarget,
    readonly stepKey: StepKey,
  ) {
    this.store = deps.store ?? pendingStore;
  }

  get snapshot(): FlowState {
    return this.state;
  }

  subscribe(fn: (s: FlowState) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit(p: Partial<FlowState>) {
    this.state = { ...this.state, ...p };
    for (const l of this.listeners) l(this.state);
  }

  private patchFile(key: string, p: Partial<FileItem>) {
    this.emit({ files: this.state.files.map((f) => (f.key === key ? { ...f, ...p } : f)) });
  }

  get busy(): boolean {
    return this.state.phase === 'uploading' || this.state.phase === 'posting';
  }

  addFiles(files: File[]) {
    if (this.busy) return;
    const items = files.map((file) => {
      const key = `f${++keySeq}`;
      this.blobs.set(key, file);
      return { key, name: file.name || 'photo.jpg', status: 'waiting' as const, progress: 0 };
    });
    this.emit({ files: [...this.state.files, ...items], phase: this.state.phase === 'confirm_no_photo' ? 'idle' : this.state.phase });
  }

  removeFile(key: string) {
    if (this.busy) return;
    const f = this.state.files.find((x) => x.key === key);
    if (!f || f.status === 'done') return;
    this.blobs.delete(key);
    this.emit({ files: this.state.files.filter((x) => x.key !== key) });
  }

  /** First press. With zero photos it asks for confirmation once (warn, but allow). */
  async submit(opts: SubmitOptions = {}): Promise<FlowState> {
    if (this.busy || this.state.phase === 'done' || this.state.phase === 'queued') return this.state;
    if (this.state.files.length === 0 && !opts.confirmNoPhoto) {
      this.emit({ phase: 'confirm_no_photo' });
      return this.state;
    }
    const now = this.deps.now?.() ?? new Date().toISOString();
    const note = opts.note?.trim();
    this.body = {
      step_key: this.stepKey,
      at: now,
      ...(note ? { note } : {}),
      ...(opts.coords ? { lat: opts.coords.lat, lng: opts.coords.lng } : {}),
      document_ids: [],
    };
    this.pendingId = (this.deps.newId ?? defaultId)();
    markBusy(this.pendingId);
    this.save();
    return this.run();
  }

  /** The sheet closed. A step left unsent (offline, failed upload) is now the banner's to send. */
  dispose() {
    if (this.pendingId) unmarkBusy(this.pendingId);
  }

  /** «Повторити»: re-upload only the failed photos, or re-post the step. */
  async retry(): Promise<FlowState> {
    if (this.busy || !this.body) return this.state;
    if (this.state.phase !== 'upload_failed' && this.state.phase !== 'failed') return this.state;
    return this.run();
  }

  private save() {
    if (!this.pendingId || !this.body) return;
    const ids = this.state.files.filter((f) => f.documentId).map((f) => f.documentId!);
    this.body = { ...this.body, document_ids: ids };
    this.store.put({ id: this.pendingId, target: this.target, body: this.body, created_at: this.body.at ?? '' });
  }

  private async run(): Promise<FlowState> {
    const todo = this.state.files.filter((f) => f.status !== 'done');
    if (todo.length) {
      this.emit({ phase: 'uploading', error: null });
      for (const f of todo) await this.uploadOne(f.key);
      if (this.state.files.some((f) => f.status !== 'done')) {
        this.emit({ phase: 'upload_failed' });
        return this.state;
      }
    }
    return this.post();
  }

  private async uploadOne(key: string) {
    const file = this.blobs.get(key);
    if (!file) {
      this.patchFile(key, { status: 'failed', error: new Error('file is gone') });
      return;
    }
    try {
      this.patchFile(key, { status: 'preparing', progress: 0, error: undefined });
      const p = await this.deps.prepare(file);
      this.patchFile(key, { status: 'uploading' });
      const ticket = await this.deps.uploadUrl(this.target, {
        document_type_key: 'photo',
        file_name: p.name,
        mime: p.mime,
        size: p.blob.size,
      });
      await this.deps.put(ticket.upload_url, p.blob, ticket.headers?.['Content-Type'] ?? p.mime, (x) =>
        this.patchFile(key, { progress: x }),
      );
      await this.deps.complete(ticket.document_id);
      this.patchFile(key, { status: 'done', progress: 1, documentId: ticket.document_id });
      this.save();
    } catch (e) {
      this.patchFile(key, { status: 'failed', error: e });
    }
  }

  private async post(): Promise<FlowState> {
    this.save();
    this.emit({ phase: 'posting', error: null });
    try {
      const step = await this.deps.addStep(this.target, this.body!);
      if (this.pendingId) {
        this.store.remove(this.pendingId);
        unmarkBusy(this.pendingId);
      }
      this.emit({ phase: 'done', step });
    } catch (e) {
      // offline: the step stays in localStorage and goes out with the next flush
      const queued = isNetworkError(e);
      if (queued && this.pendingId) unmarkBusy(this.pendingId);
      this.emit({ phase: queued ? 'queued' : 'failed', error: e });
    }
    return this.state;
  }
}

/** Send every step that is waiting in localStorage. Returns how many were posted. */
export async function flushPending(addStep: StepDeps['addStep'], store: PendingStore = pendingStore): Promise<number> {
  let sent = 0;
  const busy = usePending.getState().busy;
  for (const p of store.list()) {
    if (busy.includes(p.id)) continue; // an open sheet is sending it right now
    try {
      await addStep(p.target, p.body);
      store.remove(p.id);
      sent++;
    } catch (e) {
      if (isNetworkError(e) || (isApiError(e) && e.status >= 500)) break; // offline / server down — try later
      // the server refused it for good (handover revoked, 403, 422…): do not loop forever
      store.remove(p.id);
    }
  }
  return sent;
}
